import {
	Button,
	Grid,
	IconButton,
	InputAdornment,
	TextField,
	Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { useContextMenuMESF } from "../../../../controls";
import { LazyLoading } from "../../../../controls/LazyLoading/LazyLoading";
import { ErrorModal } from "../../../../components/modals/error-modal";
import { useEmailTopicsContext } from "../context/EmailTopicsContext";
import { EmailTopic } from "../models/EmailTopic";
import { setEmailTopicEnable } from "../repositories/EmailTopicsRepository";
import { useStyles } from "../styles/useStyles";
import { RecipientTypeLegend } from "./RecipientTypeChips";
import { useEmailTopicsOptionsFunctions } from "./customHooks/useEmailTopicsOptionsFunctions";
import { useTableData } from "./customHooks/useTableEmailTopics";
import { CreateEmailTopic } from "./upsertDelete/CreateEmailTopic";
import { DeleteEmailTopic } from "./upsertDelete/DeleteEmailTopic";
import { EditEmailTopic } from "./upsertDelete/EditEmailTopic";

interface Props {
	isLoading: boolean;
	getEmailTopicsFromAPI: () => void;
}

export const TableEmailTopics = ({
	isLoading,
	getEmailTopicsFromAPI,
}: Props) => {
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const {
		state: { topics },
		actions: { setTopicSelected },
	} = useEmailTopicsContext();
	const classes = useStyles();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [filterValue, setFilterValue] = useState("");
	const [error, setError] = useState("");
	const [openModalNew, setOpenModalNew] = useState(false);
	const [openModalEditTopic, setOpenModalEditTopic] = useState(false);
	const [openModalDeleteTopic, setOpenModalDeleteTopic] = useState(false);

	// Enabled is a switch on the row, so it saves on the spot instead of going through the form.
	const enableEmailTopic = async (topic: EmailTopic, enabled: boolean) => {
		try {
			await setEmailTopicEnable({
				emailTopicId: topic.EmailTopicId,
				enabled,
			});
			getEmailTopicsFromAPI();
		} catch (e: any) {
			setError(e?.message || "Error updating the email topic");
		}
	};

	const { getMenuOptions } = useEmailTopicsOptionsFunctions({
		setTopicSelected,
		setShowDeleteModal: setOpenModalDeleteTopic,
		setShowCreateModal: setOpenModalNew,
		setShowEditModal: setOpenModalEditTopic,
	});

	const { columnDefs } = useTableData({
		setOpenModalEditTopic,
		setOpenModalDeleteTopic,
		enableEmailTopic,
		showContextMenu,
	});

	const loadingOverlayComponent = () => (
		<div
			style={{
				width: "100%",
				height: "100%",
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<LazyLoading />
		</div>
	);

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (string | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableEmailTopics");
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "TableEmailTopics",
			getOptions: getMenuOptions,
		});
	}, []);

	// hideOverlay() also clears the "no rows" overlay, and this table only mounts once loading is
	// done, so calling it unconditionally hid the empty-state message for good.
	useEffect(() => {
		if (!gridApi) return;
		if (isLoading) {
			gridApi.showLoadingOverlay();
		} else if (topics.length === 0) {
			gridApi.showNoRowsOverlay();
		} else {
			gridApi.hideOverlay();
		}
	}, [isLoading, gridApi, topics.length]);

	return (
		<Grid
			container
			justifyContent="center"
			alignItems="center"
			className={classes.root}
		>
			<ErrorModal error={error} onHide={() => setError("")} />
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<Typography
					variant="h5"
					style={{ margin: "10px 0 6px", fontWeight: 600 }}
				>
					Email Topics
				</Typography>
			</Grid>
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<TextField
					fullWidth
					label="Search"
					variant="outlined"
					size="small"
					margin="dense"
					value={filterValue}
					onChange={(event) => {
						setFilterValue(event.target.value);
						gridApi?.setQuickFilter(event.target.value);
					}}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<IconButton edge="start" type="submit">
									<SearchIcon />
								</IconButton>
							</InputAdornment>
						),
					}}
				/>
			</Grid>

			<Grid
				item
				md={12}
				xs={12}
				style={{ padding: "0 15px" }}
				className={classes.relative}
			>
				<CreateEmailTopic
					show={openModalNew}
					onHide={(shouldUpdate: boolean) => {
						setOpenModalNew(false);
						if (shouldUpdate) getEmailTopicsFromAPI();
					}}
				/>
				<EditEmailTopic
					show={openModalEditTopic}
					onHide={(shouldUpdate: boolean) => {
						setOpenModalEditTopic(false);
						if (shouldUpdate) getEmailTopicsFromAPI();
					}}
				/>
				<DeleteEmailTopic
					show={openModalDeleteTopic}
					onHide={(shouldUpdate: boolean) => {
						setOpenModalDeleteTopic(false);
						if (shouldUpdate) getEmailTopicsFromAPI();
					}}
				/>
				<Grid
					item
					md={12}
					xs={12}
					style={{
						height: "calc(100vh - 360px)",
						minHeight: 240,
						margin: "6px 0 10px",
					}}
				>
					<div
						style={{ height: "100%", width: "100%" }}
						className="ag-theme-balham"
					>
						<AgGridReact
							rowData={topics}
							columnDefs={columnDefs}
							defaultColDef={{
								sortable: true,
								filter: true,
								resizable: true,
								suppressMenu: true,
							}}
							rowHeight={40}
							headerHeight={34}
							animateRows={true}
							loadingOverlayComponent={loadingOverlayComponent}
							overlayNoRowsTemplate={
								'<span class="ag-overlay-no-rows-center">No email topics yet</span>'
							}
							getContextMenuItems={(e: any) => getContextMenuItems(e)}
							statusBar={{
								statusPanels: [
									{ statusPanel: "agTotalRowCountComponent", align: "left" },
									{ statusPanel: "agFilteredRowCountComponent", align: "left" },
									{ statusPanel: RecipientTypeLegend, align: "right" },
								],
							}}
							rowSelection="single"
							onRowDoubleClicked={(event) => {
								setTopicSelected(event.data as EmailTopic);
								setOpenModalEditTopic(true);
							}}
							onGridReady={(params) => setGridApi(params.api)}
						/>
					</div>
				</Grid>
				<Grid container justifyContent="flex-end">
					<Grid item md={2} xs={12} className={classes.btnModal}>
						<Button
							variant="contained"
							color="primary"
							onClick={() => setOpenModalNew(!openModalNew)}
							fullWidth
							style={{ marginTop: 10 }}
						>
							NEW TOPIC
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
